import React from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { PageHeader } from "component/shared/page-header";
import { FormField, FormSelectField } from "component/shared/form-fields";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { Switch } from "component/shared/switch";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { RootState } from "redux/store";
import { getUserUnsafe } from "redux/selector";
import { saveUser } from "redux/slice/authentication";
import { resendTextingOtpVerification } from "redux/slice/campaign-campaign-messages";
import { PageProps } from "shared/page-props";
import { User, UserPartial } from "model/user";
import { TimeZoneType } from "type/time-zone";
import { displayStatus } from "shared/texting-status";
import { formatPhoneInput, isValidPhone, isValidTaxId, normalizeTaxId } from "shared/texting-fields";
import { OptIn } from "./opt-in";
import { SetupDialog } from "./setup-dialog";
import { BUSINESS_TYPE_ERROR, LEGAL_NAME_ERROR, PHONE_ERROR, TAX_ID_ERROR, TIME_ZONE_ERROR } from "./setup-dialog/steps";
import { StatusBanner } from "./status-banner";
import { StatusTag } from "./status-tag";
import { ConfirmDialog } from "./confirm-dialog";
import { BusinessTaxId, BusinessTaxIdErrors, BusinessTaxIdForm } from "./business-tax-id";
import { ConfigurationForm, configurationPayload } from "./payload";
import { styles } from "./style";

interface OwnProps extends PageProps, Router.RouteComponentProps {}

const mapStateToProps = (state: RootState) => ({
	user: getUserUnsafe(state) as User,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	saveUser,
	resendTextingOtpVerification,
}, dispatch);

interface Props extends
	OwnProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {}

type Form = ConfigurationForm;

interface State {
	form: Form;
	/** Remounts the Business Tax ID section so a revealed tax ID is forgotten after Save or Cancel. */
	taxIdKey: number;
	dialogOpen: boolean;
	submitted: boolean;
	saving: boolean;
	saveSuccess: boolean;
	saveError: string | null;
	pauseConfirmOpen: boolean;
	removeTaxIdConfirmOpen: boolean;
	activeBannerDismissed: boolean;
	otpSent: boolean;
	otpSnackbar?: string;
}

const taxIdFromUser = (user: User): BusinessTaxIdForm => ({
	hasTaxId: !!user.textMessageHasTaxId,
	taxIdType: user.textMessageTaxIdType || user.textMessageTaxIdDefaultType || "EIN",
	taxId: "",
	taxIdEdited: false,
	businessType: user.textMessageBusinessType || "",
	legalBusinessName: user.textMessageLegalBusinessName || "",
});

const formFromUser = (user: User): Form => ({
	mobile: formatPhoneInput(user.textMessageVerificationMobile),
	callForwarding: formatPhoneInput(user.textMessageCallForwarding),
	timeZone: user.tcpaCompliantTimeZone || "",
	...taxIdFromUser(user),
});

const sameForm = (a: Form, b: Form) => (Object.keys(a) as (keyof Form)[]).every((key) => a[key] === b[key]);

type Errors = BusinessTaxIdErrors & Partial<Record<"mobile" | "callForwarding" | "timeZone", string>>;

const formErrors = (form: Form, storedMasked?: string | null): Errors => {
	const errors: Errors = {};
	if (!isValidPhone(form.mobile)) {
		errors.mobile = PHONE_ERROR;
	}
	if (!isValidPhone(form.callForwarding)) {
		errors.callForwarding = PHONE_ERROR;
	}
	if (!TimeZoneType.getById(form.timeZone)) {
		errors.timeZone = TIME_ZONE_ERROR;
	}
	if (form.hasTaxId) {
		if (form.taxIdEdited ? !isValidTaxId(form.taxId) : !storedMasked) {
			errors.taxId = TAX_ID_ERROR;
		}
		if (!form.businessType) {
			errors.businessType = BUSINESS_TYPE_ERROR;
		}
		if (!form.legalBusinessName.trim()) {
			errors.legalBusinessName = LEGAL_NAME_ERROR;
		}
	}
	return errors;
};

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			form: formFromUser(props.user),
			taxIdKey: 0,
			dialogOpen: false,
			submitted: false,
			saving: false,
			saveSuccess: false,
			saveError: null,
			pauseConfirmOpen: false,
			removeTaxIdConfirmOpen: false,
			activeBannerDismissed: false,
			otpSent: false,
		};
	}

	public componentDidUpdate(prevProps: Props) {
		const { user } = this.props;
		// A save elsewhere (the campaign texts switch, the wizard) refreshes the form unless it has edits.
		if (user !== prevProps.user && user && prevProps.user && sameForm(this.state.form, formFromUser(prevProps.user))) {
			this.setState({ form: formFromUser(user) });
		}
	}

	private isDirty = () => !sameForm(this.state.form, formFromUser(this.props.user));

	private update = (patch: Partial<Form>) => {
		this.setState(({ form }) => ({ form: { ...form, ...patch }, saveError: null }));
	};

	private save = async (user: UserPartial) => {
		await (this.props.saveUser({ user }) as unknown as { unwrap: () => Promise<unknown> }).unwrap();
	};

	// The switch saves on its own, apart from Save Changes, and always from the stored user.
	private setCampaignTexts = async (enabled: boolean) => {
		const { user } = this.props;
		this.setState({ pauseConfirmOpen: false });
		try {
			await this.save({ ...user, dripMMSOptOutDate: enabled ? null : new Date() });
		} catch{
			this.setState({ saveError: "We couldn't update campaign texts. Please try again." });
		}
	};

	private resendVerification = async () => {
		try {
			await (this.props.resendTextingOtpVerification() as unknown as { unwrap: () => Promise<unknown> }).unwrap();
			this.setState({ otpSent: true, otpSnackbar: "Verification Code Sent" });
		} catch{
			this.setState({ otpSnackbar: "We couldn't resend the verification text. Please try again." });
		}
	};

	private handleSave = async () => {
		const { user } = this.props;
		const { form } = this.state;
		if (Object.keys(formErrors(form, user.textMessageTaxIdMasked)).length) {
			this.setState({ submitted: true });
			return;
		}
		const update = configurationPayload(user, form);
		this.setState({ saving: true, saveError: null });
		try {
			await this.save(update);
			this.setState(({ taxIdKey }, props) => ({
				form: formFromUser(props.user),
				taxIdKey: taxIdKey + 1,
				saving: false,
				submitted: false,
				saveSuccess: true,
			}));
		} catch(error) {
			this.setState({ saving: false, saveError: (error as Error)?.message || "We couldn't save your changes. Please try again." });
		}
	};

	private handleCancel = () => {
		this.setState(({ taxIdKey }) => ({
			form: formFromUser(this.props.user),
			taxIdKey: taxIdKey + 1,
			submitted: false,
			saveError: null,
		}));
	};

	private answerTaxIdNo = () => {
		if (this.props.user.textMessageHasTaxId) {
			this.setState({ removeTaxIdConfirmOpen: true });
		} else {
			this.removeTaxId();
		}
	};

	private removeTaxId = () => {
		const { user } = this.props;
		this.setState({ removeTaxIdConfirmOpen: false });
		this.update({
			hasTaxId: false,
			taxIdType: user.textMessageTaxIdDefaultType || "EIN",
			taxId: "",
			taxIdEdited: false,
			businessType: "",
			legalBusinessName: "",
		});
	};

	private answerTaxIdYes = () => {
		const { user } = this.props;
		// Back to the stored answer when it was Yes; otherwise empty, required fields.
		this.update(user.textMessageHasTaxId ? taxIdFromUser(user) : { hasTaxId: true });
	};

	private renderConfiguration(errors: Errors) {
		const { user, classes = {} } = this.props;
		const { form } = this.state;
		const status = displayStatus(user);
		return (
			<div className={classes.section}>
				<Mui.Typography className={classes.subheading}>Configuration</Mui.Typography>
				<div className={classes.statusField}>
					<Mui.Typography className={classes.label}>Status</Mui.Typography>
					{status && <StatusTag status={status} />}
				</div>
				<div className={classes.readOnlyField}>
					<Mui.Typography className={classes.label}>Texting Phone Number</Mui.Typography>
					<Mui.Typography className={classes.value}>{formatPhoneInput(user.textMessagePhoneNumber) || "—"}</Mui.Typography>
				</div>
				<div className={classes.field}>
					<FormField
						id="texting-mobile"
						label="Mobile Phone Number"
						required
						value={form.mobile}
						onChange={(event) => this.update({ mobile: formatPhoneInput(event.target.value) })}
						error={!!errors.mobile}
						helperText={errors.mobile}
					/>
				</div>
				<div className={classes.field}>
					<FormField
						id="texting-call-forwarding"
						label="Call Forwarding Number"
						required
						value={form.callForwarding}
						onChange={(event) => this.update({ callForwarding: formatPhoneInput(event.target.value) })}
						error={!!errors.callForwarding}
						helperText={errors.callForwarding}
					/>
				</div>
				<div className={classes.field}>
					<FormSelectField
						label="Time Zone"
						required
						value={form.timeZone}
						onChange={(timeZone) => this.update({ timeZone })}
						options={TimeZoneType.values()}
						valueExtractor={(option) => option.id}
						labelExtractor={(option) => option.label}
						error={!!errors.timeZone}
						helperText={errors.timeZone}
					/>
				</div>
			</div>
		);
	}

	public render() {
		const { user, history, classes = {} } = this.props;
		const {
			form,
			taxIdKey,
			dialogOpen,
			submitted,
			saving,
			saveSuccess,
			saveError,
			pauseConfirmOpen,
			removeTaxIdConfirmOpen,
			activeBannerDismissed,
			otpSent,
			otpSnackbar,
		} = this.state;
		const title = "Texting";
		const status = displayStatus(user);
		const errors = submitted ? formErrors(form, user.textMessageTaxIdMasked) : {};
		const hasErrors = Object.keys(errors).length > 0;
		const showResend = !!user.textMessageBrandingStatus?.unverified;
		const setUp = !!(user.textMessageOptIn && status);
		return (
			<DashboardLayout
				permitted={user.permissions.dripMms}
				title={title}
				header={
					<PageHeader
						title={title}
						description={setUp
							? "Send automated text messages to leads who opt in, using Smart Content from Marketing Automation."
							: "Automatically text leads who opt in to your drip campaigns."}
					/>
				}
			>
				<SetupDialog
					open={dialogOpen}
					onClose={() => this.setState({ dialogOpen: false })}
				/>
				{setUp && status ? (
					<>
						{!(status === "ACTIVE" && activeBannerDismissed) && (
							<StatusBanner
								status={status}
								onDismiss={() => this.setState({ activeBannerDismissed: true })}
								action={showResend && (
									otpSent ? (
										<span className={classes.otpSent}>
											<Icons.Check size={16} className={classes.otpSentIcon} aria-hidden="true" />
											Verification text sent. Reply “YES”.
										</span>
									) : (
										<Mui.Button variant="text" className={classes.resend} onClick={this.resendVerification}>
											Resend Verification Text
										</Mui.Button>
									)
								)}
							/>
						)}
						<Mui.Box mb={2}>
							<FullPageCard>
								<div className={classes.card}>
									<Mui.Typography className={classes.heading}>Texting Setup</Mui.Typography>
									<div className={classes.cardContent}>
										<div className={classes.section}>
											<Mui.Typography className={classes.subheading}>Campaign Texting</Mui.Typography>
											<Switch
												checked={!user.dripMMSOptOutDate}
												onChange={(enabled) => enabled ? this.setCampaignTexts(true) : this.setState({ pauseConfirmOpen: true })}
												label="Enable campaign texts"
												description="When enabled, leads who opt in receive texts as part of your drip campaigns, sent from a dedicated number. Disabling pauses campaign texts — your number and setup are kept, so you can resume anytime."
											/>
										</div>
										<Mui.Divider className={classes.divider} />
										{this.renderConfiguration(errors)}
									</div>
								</div>
							</FullPageCard>
						</Mui.Box>
						<Mui.Box mb={2}>
							<FullPageCard>
								<BusinessTaxId
									key={taxIdKey}
									form={form}
									storedMasked={user.textMessageTaxIdMasked}
									errors={errors}
									onChange={this.update}
									onAnswerNo={this.answerTaxIdNo}
									onAnswerYes={this.answerTaxIdYes}
								/>
							</FullPageCard>
						</Mui.Box>
						<SettingsStickyFooter
							dirty={this.isDirty()}
							saving={saving}
							authError={hasErrors ? "invalid" : null}
							saveSuccess={saveSuccess}
							saveError={saveError}
							onSave={this.handleSave}
							onCancel={this.handleCancel}
							onSaveFeedbackClose={() => this.setState({ saveSuccess: false, saveError: null })}
						/>
						<ConfirmDialog
							open={pauseConfirmOpen}
							id="pause-texting"
							title="Pause texting?"
							confirmLabel="Pause Texting"
							onConfirm={() => this.setCampaignTexts(false)}
							onCancel={() => this.setState({ pauseConfirmOpen: false })}
						>
							<Mui.Typography>
								Campaign texts will stop sending until you turn this back on. Your number and setup stay in place.
							</Mui.Typography>
						</ConfirmDialog>
						<ConfirmDialog
							open={removeTaxIdConfirmOpen}
							id="remove-tax-id"
							title="Remove Business Tax ID?"
							confirmLabel="Remove Tax ID"
							onConfirm={this.removeTaxId}
							onCancel={() => this.setState({ removeTaxIdConfirmOpen: false })}
						>
							<Mui.Typography>
								Switching to 'No, my business does not have a tax ID' will clear your EIN or Canadian BN and Legal Business Name.
							</Mui.Typography>
							<Mui.Typography>
								You'll need to re-enter this information if you add a tax ID again later.
							</Mui.Typography>
						</ConfirmDialog>
						<Mui.Snackbar
							anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
							open={!!otpSnackbar}
							onClose={() => this.setState({ otpSnackbar: undefined })}
							autoHideDuration={6000}
							message={otpSnackbar}
						/>
						<UnsavedChangesPrompt isDirty={this.isDirty} history={history} />
					</>
				) : (
					<OptIn onSubmit={() => this.setState({ dialogOpen: true })} />
				)}
			</DashboardLayout>
		);
	}
}

export const CampaignTextingPage = Router.withRouter(
	withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles)
);
